import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["prev", "next", "glide", "bullets"]
  static values = { perView: Number, totalSlides: Number, cardType: String, peekAfter: Number }

  initialize() {
    this._boundUpdatePerView = this.updatePerView.bind(this)
  }

  connect() {
    this.updatePerView()
    window.addEventListener("resize", this._boundUpdatePerView)
    this.initializeGlide()
  }

  disconnect() {
    window.removeEventListener("resize", this._boundUpdatePerView)
  }

  async initializeGlide() {
    if (!this.hasGlideTarget) {
      return
    }

    const { default: Glide } = await import("@glidejs/glide")

    this.glide = new Glide(this.glideTarget, {
      type: "slider",
      startAt: 0,
      perView: this.perViewValue,
      gap: 24,
      peek: { before: 0, after: this.hasPeekAfterValue ? this.peekAfterValue : 280 },
      autoplay: false,
      animationDuration: 200,
      animationTimingFunc: "ease-out",
      rewind: true,
      bound: true,
      breakpoints: {
        640: { perView: 1, peek: { before: 0, after: 60 } },
        768: { perView: 2, peek: { before: 0, after: 80 } },
        1024: { perView: 3, peek: { before: 0, after: this.hasPeekAfterValue ? Math.round(this.peekAfterValue * 0.57) : 160 } },
        1280: { perView: 3, peek: { before: 0, after: this.hasPeekAfterValue ? this.peekAfterValue : 280 } }
      }
    }).mount()

    // Reveal the track immediately after mount
    const track = this.glideTarget.querySelector(".glide__track")
    if (track) track.style.opacity = "1"

    // Build bullets now that Glide is mounted so click handlers work
    this.updateBullets()

    this.glide.on(["mount.after"], () => {
      this.updateActiveBullet()
      // Wait for fonts/images to settle before measuring
      requestAnimationFrame(() => requestAnimationFrame(() => this.equalizeCardHeights()))
    })
    this.glide.on(["run"], () => this.updateActiveBullet())

    // Re-equalize once everything (fonts, bg images) is fully loaded
    window.addEventListener("load", () => this.equalizeCardHeights(), { once: true })
  }

  equalizeCardHeights() {
    if (!this.hasGlideTarget) return
    const cards = this.glideTarget.querySelectorAll(".glide__slide")
    // Reset so we measure natural heights
    cards.forEach(c => { c.style.height = "auto" })
    let maxHeight = 0
    cards.forEach(c => {
      if (c.offsetHeight > maxHeight) maxHeight = c.offsetHeight
    })
    if (maxHeight > 0) {
      cards.forEach(c => { c.style.height = maxHeight + "px" })
    }
  }

  updateActiveBullet() {
    if (!this.hasBulletsTarget || !this.glide) return
    const bullets = this.bulletsTarget.querySelectorAll(".glide__bullet")
    const current = Math.min(this.glide.index, bullets.length - 1)
    bullets.forEach((bullet, i) => {
      bullet.classList.toggle("glide__bullet--active", i === current)
    })
  }

  updatePerView() {
    const screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
    let perView = 3

    if (screenWidth <= 640) {
      perView = 1
    } else if (screenWidth <= 768) {
      perView = 2
    } else {
      perView = 3
    }

    this.perViewValue = perView

    if (this.glide) {
      this.glide.update({ perView: this.perViewValue })
      this.equalizeCardHeights()
    }

    this.updateBullets()
  }

  updateBullets() {
    if (!this.hasBulletsTarget || !this.hasTotalSlidesValue) {
      return
    }

    const totalSlides = this.totalSlidesValue
    const perView = this.perViewValue || 3
    // With bound:true, Glide's max index is totalSlides - perView,
    // so only (totalSlides - perView + 1) distinct positions exist.
    const numberOfPages = Math.max(1, totalSlides - perView + 1)

    if (numberOfPages <= 0) {
      this.bulletsTarget.innerHTML = ""
      return
    }

    const bulletsContainer = this.bulletsTarget
    bulletsContainer.innerHTML = ""

    for (let i = 0; i < numberOfPages; i++) {
      const indicatorBullet = document.createElement("button")
      indicatorBullet.className = "glide__bullet" + (i === 0 ? " glide__bullet--active" : "")
      indicatorBullet.setAttribute("data-glide-dir", `=${i}`)
      // Manually wire click so it works whether or not Glide picked up the controls
      indicatorBullet.addEventListener("click", () => {
        if (this.glide) {
          this.glide.go(`=${i}`)
          this.updateActiveBullet()
        }
      })
      bulletsContainer.appendChild(indicatorBullet)
    }
  }

  prev() {
    if (!this.glide) return
    this.glide.go("<")
  }

  next() {
    if (!this.glide) return
    this.glide.go(">")
  }
}
